import { useState } from "react";
import { Drop, CompletedWalk } from "./types";
import { FONTS } from "./data/affirmations";
import { SplashScreen } from "./components/SplashScreen";
import { AuthScreen } from "./components/AuthScreen";
import { WalkScreen } from "./components/WalkScreen";
import { WalkComplete } from "./components/WalkComplete";
import { DropsTab } from "./components/DropsTab";
import { ClassicsTab } from "./components/ClassicsTab";
import { ZonesTab } from "./components/ZonesTab";
import { StatsTab } from "./components/StatsTab";
import { LeaderboardTab } from "./components/LeaderboardTab";
import { useFirestore } from "./hooks/useFirestore";

type Screen = "splash" | "auth" | "app" | "walk" | "complete";
type Tab = "drops" | "classics" | "map" | "leaderboard" | "stats";

const TABS: { id: Tab; label: string; emoji: string }[] = [
  { id: "drops", label: "Drops", emoji: "⚡" },
  { id: "classics", label: "Classics", emoji: "🌅" },
  { id: "map", label: "Zones", emoji: "🗺️" },
  { id: "leaderboard", label: "Squad", emoji: "👯" },
  { id: "stats", label: "Stats", emoji: "📊" },
];

export default function App() {
  const [screen, setScreen] = useState<Screen>("splash");
  const [activeTab, setActiveTab] = useState<Tab>("drops");
  const [userId, setUserId] = useState<string | null>(null);
  const [activeDrop, setActiveDrop] = useState<Drop | null>(null);
  const [lastWalkTime, setLastWalkTime] = useState(0);
  const [lastWalkSteps, setLastWalkSteps] = useState(0);

  const { userData, loading, saveWalk, saveName } = useFirestore(userId);

   const handleAuth = async (uid: string, name: string) => {
    setUserId(uid);
    await saveName(uid, name);
    setScreen("app");
  };

  const handleStartWalk = (drop: Drop) => {
    setActiveDrop(drop);
    setScreen("walk");
  };

  const handleEndWalk = async (walkTime: number, steps: number) => {
    setLastWalkTime(walkTime);
    setLastWalkSteps(steps);
    if (activeDrop && userId) {
      const now = new Date();
      const dateStr = now.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
      const walk: CompletedWalk = {
        title: activeDrop.title,
        emoji: activeDrop.emoji,
        neighbourhood: activeDrop.neighbourhood,
        distance: activeDrop.distance,
        time: activeDrop.time,
        date: dateStr,
      };
      await saveWalk(userId, walk, userData);
    }
    setScreen("complete");
  };

  const handleWalkDone = () => {
    setScreen("app");
    setActiveTab("stats");
  };

  if (screen === "splash") return <SplashScreen onStart={() => setScreen("auth")} />;
  if (screen === "auth") return <AuthScreen onAuth={handleAuth} />;
  if (screen === "walk" && activeDrop) return <WalkScreen drop={activeDrop} onEnd={handleEndWalk} />;
  if (screen === "complete" && activeDrop) return <WalkComplete drop={activeDrop} walkTime={lastWalkTime} steps={lastWalkSteps} onDone={handleWalkDone} />;

  return (
    <div style={{ minHeight: "100vh", background: "#FAF7F4", fontFamily: "'DM Sans',sans-serif", color: "#1a1a1a" }}>
      <style>{FONTS + `
        @keyframes fadeUp { from { opacity:0;transform:translateY(16px) } to { opacity:1;transform:translateY(0) } }
        * { box-sizing:border-box; margin:0; padding:0 }
      `}</style>

      <div style={{ position: "fixed", top: "-80px", right: "-80px", width: "300px", height: "300px", background: "radial-gradient(circle,#FADADD 0%,transparent 70%)", borderRadius: "50%", pointerEvents: "none", zIndex: 0 }} />
      <div style={{ position: "fixed", bottom: "-60px", left: "-60px", width: "260px", height: "260px", background: "radial-gradient(circle,#D4ECD4 0%,transparent 70%)", borderRadius: "50%", pointerEvents: "none", zIndex: 0 }} />

      <header style={{ padding: "28px 20px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", zIndex: 1 }}>
        <div>
          <div style={{ fontSize: "10px", letterSpacing: "4px", color: "#C9184A", textTransform: "uppercase", fontWeight: 700, fontFamily: "'DM Sans',sans-serif" }}>london</div>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontStyle: "italic", fontWeight: 900, fontSize: "30px", color: "#1a1a1a", letterSpacing: "-1px", lineHeight: 1 }}>Hot Girl Walk</h1>
        </div>
        <div style={{ textAlign: "right" }}>
          {loading ? (
            <div style={{ fontSize: "12px", color: "#bbb", fontFamily: "'DM Sans',sans-serif" }}>loading...</div>
          ) : (
            <>
              <div style={{ fontSize: "12px", color: "#C9184A", fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>hey {userData.userName} 💕</div>
              <div style={{ fontSize: "11px", color: "#bbb", fontFamily: "'DM Sans',sans-serif" }}>{userData.streak} day streak 🔥</div>
            </>
          )}
        </div>
      </header>

      <nav style={{ display: "flex", justifyContent: "center", gap: "4px", padding: "8px 16px 12px", position: "sticky", top: 0, zIndex: 10, background: "rgba(250,247,244,0.94)", backdropFilter: "blur(16px)", borderBottom: "1px solid #EDE8E3" }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{ padding: "8px 12px", borderRadius: "50px", border: "none", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", transition: "all 0.25s ease", background: activeTab === tab.id ? "#1a1a1a" : "transparent", boxShadow: activeTab === tab.id ? "0 4px 16px rgba(0,0,0,0.15)" : "none", display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", minWidth: "52px" }}>
            <span style={{ fontSize: "18px", lineHeight: 1 }}>{tab.emoji}</span>
            <span style={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", color: activeTab === tab.id ? "#fff" : "#888" }}>{tab.label}</span>
          </button>
        ))}
      </nav>

      <main style={{ padding: "20px 16px 80px", maxWidth: "480px", margin: "0 auto", position: "relative", zIndex: 1 }}>
        {activeTab === "drops" && <DropsTab onStartWalk={handleStartWalk} />}
        {activeTab === "classics" && <ClassicsTab onStartWalk={handleStartWalk} />}
        {activeTab === "map" && <ZonesTab onSelectZone={() => setActiveTab("drops")} />}
        {activeTab === "leaderboard" && <LeaderboardTab totalWalks={userData.totalWalks} totalMiles={userData.totalMiles} userName={userData.userName} />}
        {activeTab === "stats" && <StatsTab totalWalks={userData.totalWalks} totalMiles={userData.totalMiles} streak={userData.streak} completedWalks={userData.completedWalks} />}
      </main>
    </div>
  );
}
